import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { forkJoin, take } from 'rxjs';
import { InviteMemberModalComponent } from 'src/app/components/modals/invite-member-modal/invite-member-modal.component';
import {
  ColumnDefinition,
  DataColumnDefinition,
} from 'src/app/components/qs-table/column-definition.interface';
import { CompanyMemberType, CompanyType } from 'src/app/models/company.model';
import { LicenseBatchDto } from 'src/app/models/license.model';
import {
  CompanyRoleValue,
  readsDataAcrossCompanies,
  UserRole,
} from 'src/app/models/user.model';
import { MasterAdminCompanyService } from 'src/app/services/master-admin-company.service';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';

type MemberRow = CompanyMemberType;

@Component({
  selector: 'app-master-admin-company-details',
  templateUrl: './master-admin-company-details.component.html',
  styleUrls: ['./master-admin-company-details.component.scss'],
  standalone: false,
})
export class MasterAdminCompanyDetailsComponent implements OnInit {
  @ViewChild('licenseBatchesTemplate', { static: true })
  private licenseBatchesTemplate!: TemplateRef<{ $implicit: MemberRow }>;

  private readonly dialog = inject(Dialog);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly masterAdminCompanyService = inject(
    MasterAdminCompanyService,
  );
  private readonly profile = toSignal(this.store.select(selectProfileState), {
    requireSync: false,
  });

  readonly tablePaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [10, 25, 50],
  };
  readonly memberRowTestId = (member: MemberRow): string => member.userId ?? '';

  readonly loadingCompany = signal<boolean>(true);
  readonly loadingMembers = signal<boolean>(true);
  readonly savingMemberAction = signal<boolean>(false);
  readonly company = signal<CompanyType | null>(null);
  readonly members = signal<MemberRow[]>([]);
  readonly error = signal<string>('');

  readonly showResponseBanner = signal<boolean>(false);
  readonly successfulSave = signal<boolean>(false);
  readonly successMessage = signal<string>('');
  readonly errorMessage = signal<string>('');

  private readonly licenseBatchesColumn: DataColumnDefinition<MemberRow> = {
    type: 'data',
    field: 'licenseBatches',
    header: 'License Batches',
    width: '260px',
    sortFn: (a, b, multiplier) =>
      this.getLicenseBatchNames(a).localeCompare(this.getLicenseBatchNames(b)) *
      multiplier,
  };

  readonly memberColumns: ColumnDefinition<MemberRow>[] = [
    {
      type: 'data',
      field: 'firstName',
      header: 'Employee',
      format: (row) => this.formatMemberName(row),
    },
    {
      type: 'data',
      field: 'role',
      header: 'Role',
      format: (row) => this.getRoleLabel(row?.role),
    },
    {
      type: 'data',
      field: 'email',
      header: 'Email',
      format: (row) => row?.email || '-',
    },
    this.licenseBatchesColumn,
    {
      type: 'action',
      header: 'Actions',
      testId: 'actions',
      width: '72px',
      actionType: 'menu',
      actions: [
        {
          label: 'Set as Admin',
          callback: (row: MemberRow) => this.onUpdateMemberRole(row, '1'),
        },
        {
          label: 'Set as Pilot',
          testId: 'set-pilot',
          callback: (row: MemberRow) => this.onUpdateMemberRole(row, '0'),
        },
        {
          label: 'Set as Maintainer',
          testId: 'set-maintainer',
          callback: (row: MemberRow) => this.onUpdateMemberRole(row, '2'),
        },
        {
          label: 'Remove Member',
          callback: (row: MemberRow) => this.onRemoveMember(row),
        },
      ],
    },
  ];

  readonly sortedMembers = computed<MemberRow[]>(() =>
    this.members()
      .slice()
      .sort((a, b) => {
        const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return aName.localeCompare(bName);
      }),
  );

  readonly isLoading = computed<boolean>(
    () => this.loadingCompany() || this.loadingMembers(),
  );

  readonly companyId = computed<string>(
    () => this.company()?.id || this.route.snapshot.paramMap.get('id') || '',
  );

  ngOnInit(): void {
    this.licenseBatchesColumn.customTemplate = this.licenseBatchesTemplate;

    const companyId = this.route.snapshot.paramMap.get('id');
    if (!companyId) {
      this.navigateToList();
      return;
    }

    this.loadCompanyDetails(companyId);
  }

  getLicenseBatches(member: MemberRow): LicenseBatchDto[] {
    const seenIds = new Set<string>();
    return (member.licenseBatches ?? []).filter((batch) => {
      const id = batch.id?.trim();
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
  }

  getLicenseBatchLabel(batch: LicenseBatchDto): string {
    return batch.name?.trim() || batch.id?.trim() || 'License Batch';
  }

  /**
   * A support user reads a company and its members; only a masteradmin may change either. The
   * page itself is reachable by both, so every action on it is gated here.
   */
  get canManageCompany(): boolean {
    return this.profile()?.isMasteradmin ?? false;
  }

  /** {@link #memberColumns} without the action menu for a viewer who may only read. */
  get visibleMemberColumns(): ColumnDefinition<MemberRow>[] {
    return this.canManageCompany
      ? this.memberColumns
      : this.memberColumns.filter((column) => column.type !== 'action');
  }

  get canNavigateLicenseBatches(): boolean {
    const profile = this.profile();
    return (
      readsDataAcrossCompanies(profile) || profile?.role === UserRole.ADMIN
    );
  }

  getLicenseBatchRoute(batch: LicenseBatchDto): string[] {
    const prefix = readsDataAcrossCompanies(this.profile())
      ? '/admin/licenses/batches'
      : '/licenses/batches';
    return [prefix, batch.id!];
  }

  trackLicenseBatch(index: number, batch: LicenseBatchDto): string {
    return batch.id ?? `${batch.name ?? 'license-batch'}-${index}`;
  }

  private getLicenseBatchNames(member: MemberRow): string {
    return this.getLicenseBatches(member)
      .map((batch) => this.getLicenseBatchLabel(batch))
      .join(', ');
  }

  navigateToList(): void {
    this.router.navigate(['/admin/companies']);
  }

  navigateToEdit(): void {
    if (!this.companyId()) {
      return;
    }
    this.router.navigate(['/admin/companies', this.companyId(), 'edit']);
  }

  openInviteDialog(): void {
    const company = this.company();
    if (!company?.id) {
      return;
    }

    this.dialog.open(InviteMemberModalComponent, {
      data: {
        companyId: company.id,
        companyName: company.name,
      },
    });
  }

  private loadCompanyDetails(companyId: string): void {
    this.loadingCompany.set(true);
    this.loadingMembers.set(true);
    this.error.set('');

    forkJoin({
      company: this.masterAdminCompanyService.getCompany(companyId),
      members: this.masterAdminCompanyService.getCompanyMembers(companyId),
    })
      .pipe(take(1))
      .subscribe({
        next: ({ company, members }) => {
          this.updateLicenseBatchColumn(members ?? []);
          this.company.set(company);
          this.members.set(members ?? []);
          this.loadingCompany.set(false);
          this.loadingMembers.set(false);
        },
        error: (error: unknown) => {
          this.error.set(
            this.resolveErrorMessage(error, 'Failed to load company details.'),
          );
          this.loadingCompany.set(false);
          this.loadingMembers.set(false);
          this.navigateToList();
        },
      });
  }

  private updateLicenseBatchColumn(members: MemberRow[]): void {
    const hasLicenses = members.some(
      (member) => this.getLicenseBatches(member).length > 0,
    );
    const currentColumnIndex = this.memberColumns.findIndex(
      (column) => column.type === 'data' && column.field === 'licenseBatches',
    );

    if (hasLicenses && currentColumnIndex < 0) {
      const actionColumnIndex = this.memberColumns.findIndex(
        (column) => column.type === 'action',
      );
      this.memberColumns.splice(
        actionColumnIndex >= 0 ? actionColumnIndex : this.memberColumns.length,
        0,
        this.licenseBatchesColumn,
      );
    } else if (!hasLicenses && currentColumnIndex >= 0) {
      this.memberColumns.splice(currentColumnIndex, 1);
    }
  }

  private onUpdateMemberRole(row: MemberRow, role: CompanyRoleValue): void {
    if (!this.companyId() || !row?.userId) {
      return;
    }

    if (String(row.role ?? '') === String(role)) {
      return;
    }

    this.showResponseBanner.set(false);
    this.savingMemberAction.set(true);

    this.masterAdminCompanyService
      .assignCompanyRole(this.companyId(), row.userId, { role })
      .pipe(take(1))
      .subscribe({
        next: (updatedMember) => {
          this.members.update((members) =>
            members.map((member) =>
              member.userId === updatedMember.userId
                ? {
                    ...member,
                    ...updatedMember,
                    licenseBatches:
                      updatedMember.licenseBatches ?? member.licenseBatches,
                  }
                : member,
            ),
          );
          this.savingMemberAction.set(false);
          this.successfulSave.set(true);
          this.errorMessage.set('');
          this.successMessage.set('Member role updated successfully.');
          this.showResponseBanner.set(true);
        },
        error: (error: unknown) => {
          this.handleMemberActionError(error, 'Failed to update member role.');
        },
      });
  }

  private onRemoveMember(row: MemberRow): void {
    if (!this.companyId() || !row?.userId) {
      return;
    }

    const memberName = this.formatMemberName(row);
    if (
      !window.confirm(
        `Remove "${memberName}" from this company? This action cannot be undone.`,
      )
    ) {
      return;
    }

    this.showResponseBanner.set(false);
    this.savingMemberAction.set(true);

    this.masterAdminCompanyService
      .removeCompanyMember(this.companyId(), row.userId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.members.update((members) => {
            const updatedMembers = members.filter(
              (member) => member.userId !== row.userId,
            );
            this.updateLicenseBatchColumn(updatedMembers);
            return updatedMembers;
          });
          this.savingMemberAction.set(false);
          this.successfulSave.set(true);
          this.errorMessage.set('');
          this.successMessage.set('Member removed successfully.');
          this.showResponseBanner.set(true);
        },
        error: (error: unknown) => {
          this.handleMemberActionError(error, 'Failed to remove member.');
        },
      });
  }

  private handleMemberActionError(error: unknown, fallback: string): void {
    this.savingMemberAction.set(false);
    this.successfulSave.set(false);
    this.errorMessage.set(this.resolveErrorMessage(error, fallback));
    this.successMessage.set('');
    this.showResponseBanner.set(true);
  }

  private formatMemberName(row?: MemberRow): string {
    const firstName = row?.firstName?.trim() || '';
    const lastName = row?.lastName?.trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '-';
  }

  private getRoleLabel(role: CompanyRoleValue | undefined): string {
    switch (String(role)) {
      case '1':
        return UserRole.ADMIN;
      case '2':
        return UserRole.MAINTAINER;
      case '0':
      default:
        return UserRole.PILOT;
    }
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (!error) {
      return fallback;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        message?: string;
        error?: { message?: string; title?: string };
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return fallback;
  }
}
